import * as THREE from "three";
import { buildPalette, SCHEMES, SLOT_COUNT, type PaletteScheme } from "./palette";
import type { AccentOptions, DitherOptions } from "./types";

export function bayerMatrix(size: number): number[] {
  let matrix = [0];
  let order = 1;
  while (order < size) {
    const stride = order * 2;
    const next: number[] = new Array(stride * stride);
    for (let y = 0; y < order; y++) {
      for (let x = 0; x < order; x++) {
        const value = (matrix[y * order + x] as number) * 4;
        next[y * stride + x] = value;
        next[y * stride + x + order] = value + 2;
        next[(y + order) * stride + x] = value + 3;
        next[(y + order) * stride + x + order] = value + 1;
      }
    }
    matrix = next;
    order = stride;
  }
  return matrix;
}

export function bayerTexture(size: number): THREE.DataTexture {
  const matrix = bayerMatrix(size);
  const total = size * size;
  const data = new Uint8Array(total * 4);
  for (let i = 0; i < total; i++) {
    const value = Math.round((((matrix[i] as number) + 0.5) / total) * 255);
    data[i * 4] = value;
    data[i * 4 + 1] = value;
    data[i * 4 + 2] = value;
    data[i * 4 + 3] = 255;
  }
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

const FULLSCREEN_VERTEX = /* glsl */ `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const BLUR_FRAGMENT = /* glsl */ `
precision highp float;

uniform sampler2D tSource;
uniform vec2 uTexel;
uniform vec2 uDirection;
uniform float uRadius;

varying vec2 vUv;

const float WEIGHTS[5] = float[5](0.227027, 0.1945946, 0.1216216, 0.054054, 0.016216);

void main() {
  vec2 step = uDirection * uTexel * uRadius;
  vec3 sum = texture2D(tSource, vUv).rgb * WEIGHTS[0];
  for (int i = 1; i < 5; i++) {
    vec2 delta = step * float(i);
    sum += texture2D(tSource, vUv + delta).rgb * WEIGHTS[i];
    sum += texture2D(tSource, vUv - delta).rgb * WEIGHTS[i];
  }
  gl_FragColor = vec4(sum, 1.0);
}
`;

const MASK_VERTEX = /* glsl */ `
void main() {
  #include <begin_vertex>
  #include <project_vertex>
}
`;

const MASK_FRAGMENT = /* glsl */ `
precision highp float;

uniform float uSlot;
uniform float uVariant;

void main() {
  gl_FragColor = vec4(uSlot, uVariant, 1.0, 1.0);
}
`;

const COMPOSITE_FRAGMENT = /* glsl */ `
precision highp float;

uniform sampler2D tScene;
uniform sampler2D tBlur1;
uniform sampler2D tBlur2;
uniform sampler2D tBlur3;
uniform sampler2D tAccent;
uniform sampler2D tAccentBlur;
uniform sampler2D tBayer;
uniform sampler2D tPalette;
uniform sampler2D tExplored;
uniform sampler2D tDepth;
uniform vec3 uShadowTint;
uniform vec3 uHighlightTint;
uniform float uAccentStrength;
uniform float uAccentGain;

uniform float uAspect;
uniform float uViewHeight;
uniform float uViewWidth;
uniform float uClearWorld;
uniform float uBlurWorld;
uniform vec2 uPlayerXZ;
uniform float uWorldSize;
uniform mat4 uCameraMatrix;
uniform float uNear;
uniform float uFar;
uniform float uMidMix;
uniform float uFog;

uniform float uLevels;
uniform float uCell;
uniform float uExposure;
uniform float uGamma;
uniform float uContrast;
uniform float uBayerSize;

varying vec2 vUv;

void main() {
  vec3 sharp = texture2D(tScene, vUv).rgb;
  vec3 blur1 = texture2D(tBlur1, vUv).rgb;
  vec3 blur2 = texture2D(tBlur2, vUv).rgb;
  vec3 blur3 = texture2D(tBlur3, vUv).rgb;

  float depth = texture2D(tDepth, vUv).x;
  vec3 viewPosition = vec3((vUv - 0.5) * vec2(uViewWidth, uViewHeight), -(uNear + depth * (uFar - uNear)));
  vec3 worldPosition = (uCameraMatrix * vec4(viewPosition, 1.0)).xyz;
  vec2 fragmentXZ = worldPosition.xz;
  float worldDistance = length(fragmentXZ - uPlayerXZ);
  float amount = smoothstep(uClearWorld, uBlurWorld, worldDistance);

  vec3 colour = mix(sharp, blur1, smoothstep(0.0, 0.34, amount));
  colour = mix(colour, blur2, smoothstep(0.3, 0.68, amount));
  colour = mix(colour, blur3, smoothstep(0.62, 1.0, amount));

  float luma = dot(colour, vec3(0.2126, 0.7152, 0.0722)) * uExposure;
  luma = clamp(luma, 0.0, 1.0);
  luma = pow(luma, uGamma);
  luma = clamp((luma - 0.5) * uContrast + 0.5, 0.0, 1.0);

  vec2 cell = floor(gl_FragCoord.xy / uCell);
  vec2 bayerOffset = (mod(cell, uBayerSize) + 0.5) / uBayerSize;
  float threshold = texture2D(tBayer, bayerOffset).r;

  float steps = max(uLevels - 1.0, 1.0);
  float quantised = floor(luma * steps + threshold) / steps;

  vec3 shaded = mix(uShadowTint, uHighlightTint, quantised);

  vec3 mask = texture2D(tAccent, vUv).rgb;
  float accentMask = clamp(mask.b, 0.0, 1.0);
  float accentSlot = floor(mask.r * ${SLOT_COUNT}.0 + 0.5);
  vec2 paletteUv = vec2((accentSlot + 0.5) / ${SLOT_COUNT}.0, clamp(mask.g, 0.0, 1.0));
  vec4 paletteSample = texture2D(tPalette, paletteUv);
  float lit = pow(clamp(dot(sharp, vec3(0.2126, 0.7152, 0.0722)), 0.0, 1.0), 1.7);
  float accentLuma = mix(lit, 1.0, paletteSample.a);
  vec3 accentTone = paletteSample.rgb * accentLuma * uAccentGain;
  float accent = accentMask * uAccentStrength * (1.0 - smoothstep(0.35, 1.0, amount) * 0.5);
  shaded = mix(shaded, accentTone, accent);

  // fire is its own layer: it must glow warm whatever the accent is set to, and
  // a duotone has thrown the colour away by this point. It still has to obey the
  // fog though — far away it should be a soft glow, not a crisp flame outline.
  // past the blur radius the flame model should drop out entirely and leave only
  // the glow it throws on the ground, which is real lighting and blurs normally
  float softMask = clamp(texture2D(tAccentBlur, vUv).b, 0.0, 1.0);
  float fireSoftness = smoothstep(uClearWorld * 0.55, uBlurWorld * 0.9, worldDistance);
  float fireCoverage = mix(accentMask, softMask * 0.5, fireSoftness);
  float fireFade = 1.0 - smoothstep(uClearWorld, uBlurWorld * 1.15, worldDistance);
  float fire = fireCoverage * step(12.5, accentSlot) * step(accentSlot, 13.5) * fireFade;
  vec3 emberTone = paletteSample.rgb * (0.5 + accentLuma * 1.0);
  shaded = mix(shaded, emberTone, fire * 0.95);

  vec2 exploredUv = clamp(fragmentXZ / uWorldSize + 0.5, 0.0, 1.0);
  float explored = texture2D(tExplored, exploredUv).r;
  float fade = clamp((1.0 - explored) * uFog, 0.0, 1.0);
  shaded = mix(shaded, uShadowTint * 0.82, fade);

  gl_FragColor = vec4(shaded, 1.0);
}
`;

class FullscreenPass {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  readonly material: THREE.ShaderMaterial;

  constructor(fragmentShader: string, uniforms: Record<string, THREE.IUniform>) {
    this.material = new THREE.ShaderMaterial({
      vertexShader: FULLSCREEN_VERTEX,
      fragmentShader,
      depthTest: false,
      depthWrite: false,
      uniforms,
    });
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material);
    quad.frustumCulled = false;
    this.scene.add(quad);
  }
}

interface AccentMesh {
  mesh: THREE.Mesh;
  original: THREE.Material | THREE.Material[];
  mask: THREE.ShaderMaterial;
}

export interface BlurOptions {
  clearWorld: number;
  blurWorld: number;
  midMix: number;
  mipScale: number;
  strength: number;
}

export interface ViewFrame {
  worldHeight: number;
  worldWidth: number;
  playerX: number;
  playerZ: number;
  worldSize: number;
  near: number;
  far: number;
  cameraMatrix: THREE.Matrix4;
}

export class PostPipeline {
  readonly sceneTarget: THREE.WebGLRenderTarget;
  readonly blur1Target: THREE.WebGLRenderTarget;
  readonly blur2Target: THREE.WebGLRenderTarget;
  private readonly blur3Target: THREE.WebGLRenderTarget;
  private readonly maskBlur1Target: THREE.WebGLRenderTarget;
  private readonly maskBlur2Target: THREE.WebGLRenderTarget;

  private readonly renderer: THREE.WebGLRenderer;
  private readonly scratch: THREE.WebGLRenderTarget;
  private readonly blurHorizontal: FullscreenPass;
  private readonly blurVertical: FullscreenPass;
  private readonly composite: FullscreenPass;
  private readonly blur: BlurOptions;
  private readonly accentTarget: THREE.WebGLRenderTarget;
  private readonly occluderMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
  private readonly clearColour = new THREE.Color();
  private readonly maskLayer = new THREE.Layers();

  private accentMeshes: AccentMesh[] | null = null;

  private palette: THREE.DataTexture;
  private paletteScheme: PaletteScheme;
  private accentColour = 0xffffff;
  private accentSaturation = 0.72;

  private width = 1;
  private height = 1;

  constructor(
    renderer: THREE.WebGLRenderer,
    dither: DitherOptions,
    blur: BlurOptions,
    width: number,
    height: number,
  ) {
    this.renderer = renderer;
    this.blur = { ...blur };
    this.maskLayer.set(1);
    this.paletteScheme = SCHEMES.mono as PaletteScheme;
    this.palette = buildPalette(this.paletteScheme, this.accentColour, this.accentSaturation);

    const params = {
      type: THREE.HalfFloatType,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: false,
      stencilBuffer: false,
    } as const;

    const depthTexture = new THREE.DepthTexture(width, height);
    depthTexture.type = THREE.UnsignedIntType;

    this.sceneTarget = new THREE.WebGLRenderTarget(width, height, {
      ...params,
      depthBuffer: true,
      depthTexture,
    });
    this.blur1Target = new THREE.WebGLRenderTarget(width, height, params);
    this.blur2Target = new THREE.WebGLRenderTarget(width, height, params);
    this.blur3Target = new THREE.WebGLRenderTarget(width, height, params);
    this.maskBlur1Target = new THREE.WebGLRenderTarget(width, height, params);
    this.maskBlur2Target = new THREE.WebGLRenderTarget(width, height, params);
    this.scratch = new THREE.WebGLRenderTarget(width, height, params);
    this.accentTarget = new THREE.WebGLRenderTarget(width, height, {
      type: THREE.HalfFloatType,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      depthBuffer: true,
      stencilBuffer: false,
    });

    const blurUniforms = () => ({
      tSource: { value: this.sceneTarget.texture as THREE.Texture | null },
      uTexel: { value: new THREE.Vector2(1 / width, 1 / height) },
      uDirection: { value: new THREE.Vector2(1, 0) },
      uRadius: { value: blur.strength },
    });

    this.blurHorizontal = new FullscreenPass(BLUR_FRAGMENT, blurUniforms());
    this.blurVertical = new FullscreenPass(BLUR_FRAGMENT, blurUniforms());

    this.composite = new FullscreenPass(COMPOSITE_FRAGMENT, {
      tScene: { value: this.sceneTarget.texture },
      tBlur1: { value: this.scratch.texture },
      tBlur2: { value: this.blur2Target.texture },
      tBlur3: { value: this.blur3Target.texture },
      tAccent: { value: this.accentTarget.texture },
      tDepth: { value: this.sceneTarget.depthTexture },
      tAccentBlur: { value: this.maskBlur2Target.texture },
      tPalette: { value: this.palette },
      uAccentStrength: { value: 0 },
      uAccentGain: { value: 1.45 },
      uShadowTint: { value: new THREE.Color(dither.shadowTint) },
      uHighlightTint: { value: new THREE.Color(dither.highlightTint) },
      tBayer: { value: bayerTexture(dither.bayerSize) },
      uAspect: { value: 1 },
      uViewHeight: { value: 12 },
      uClearWorld: { value: blur.clearWorld },
      uBlurWorld: { value: blur.blurWorld },
      uPlayerXZ: { value: new THREE.Vector2() },
      uWorldSize: { value: 160 },
      uCameraMatrix: { value: new THREE.Matrix4() },
      uNear: { value: 0.1 },
      uFar: { value: 600 },
      uViewWidth: { value: 16 },
      tExplored: { value: null as THREE.Texture | null },
      uMidMix: { value: blur.midMix },
      uFog: { value: dither.fog },
      uLevels: { value: dither.levels },
      uCell: { value: dither.cell },
      uExposure: { value: dither.exposure },
      uGamma: { value: dither.gamma },
      uContrast: { value: dither.contrast },
      uBayerSize: { value: dither.bayerSize },
    });

    this.setSize(width, height);
  }

  configure(dither: Partial<DitherOptions>): void {
    const uniforms = this.composite.material.uniforms;
    if (dither.levels !== undefined) uniforms.uLevels!.value = dither.levels;
    if (dither.cell !== undefined) uniforms.uCell!.value = dither.cell;
    if (dither.exposure !== undefined) uniforms.uExposure!.value = dither.exposure;
    if (dither.gamma !== undefined) uniforms.uGamma!.value = dither.gamma;
    if (dither.contrast !== undefined) uniforms.uContrast!.value = dither.contrast;
    if (dither.fog !== undefined) uniforms.uFog!.value = dither.fog;
    if (dither.shadowTint !== undefined) {
      (uniforms.uShadowTint!.value as THREE.Color).set(dither.shadowTint);
    }
    if (dither.highlightTint !== undefined) {
      (uniforms.uHighlightTint!.value as THREE.Color).set(dither.highlightTint);
    }
    if (dither.bayerSize !== undefined) {
      uniforms.uBayerSize!.value = dither.bayerSize;
      uniforms.tBayer!.value = bayerTexture(dither.bayerSize);
    }
  }

  configureBlur(blur: Partial<BlurOptions>): void {
    Object.assign(this.blur, blur);
    const uniforms = this.composite.material.uniforms;
    if (blur.clearWorld !== undefined) uniforms.uClearWorld!.value = blur.clearWorld;
    if (blur.blurWorld !== undefined) uniforms.uBlurWorld!.value = blur.blurWorld;
    if (blur.midMix !== undefined) uniforms.uMidMix!.value = blur.midMix;
    if (blur.strength !== undefined) {
      this.blurHorizontal.material.uniforms.uRadius!.value = blur.strength;
      this.blurVertical.material.uniforms.uRadius!.value = blur.strength;
    }
  }

  setSize(width: number, height: number): void {
    this.width = Math.max(1, Math.floor(width));
    this.height = Math.max(1, Math.floor(height));
    const scale = Math.max(1, this.blur.mipScale);
    const blurWidth = Math.max(1, Math.floor(this.width / scale));
    const blurHeight = Math.max(1, Math.floor(this.height / scale));

    this.sceneTarget.setSize(this.width, this.height);
    this.blur1Target.setSize(blurWidth, blurHeight);
    this.blur2Target.setSize(blurWidth, blurHeight);
    this.blur3Target.setSize(blurWidth, blurHeight);
    this.maskBlur1Target.setSize(blurWidth, blurHeight);
    this.maskBlur2Target.setSize(blurWidth, blurHeight);
    this.accentTarget.setSize(this.width, this.height);
    this.scratch.setSize(blurWidth, blurHeight);

    this.composite.material.uniforms.uAspect!.value = this.width / this.height;

    const texel = new THREE.Vector2(1 / blurWidth, 1 / blurHeight);
    (this.blurHorizontal.material.uniforms.uTexel!.value as THREE.Vector2).copy(texel);
    (this.blurVertical.material.uniforms.uTexel!.value as THREE.Vector2).copy(texel);
  }

  attachExplored(texture: THREE.Texture): void {
    this.composite.material.uniforms.tExplored!.value = texture;
  }

  setView(frame: ViewFrame): void {
    const uniforms = this.composite.material.uniforms;
    uniforms.uViewHeight!.value = frame.worldHeight;
    uniforms.uViewWidth!.value = frame.worldWidth;
    uniforms.uWorldSize!.value = frame.worldSize;
    uniforms.uNear!.value = frame.near;
    uniforms.uFar!.value = frame.far;
    (uniforms.uPlayerXZ!.value as THREE.Vector2).set(frame.playerX, frame.playerZ);
    (uniforms.uCameraMatrix!.value as THREE.Matrix4).copy(frame.cameraMatrix);
  }

  setAccent(accent: Partial<AccentOptions>): void {
    const uniforms = this.composite.material.uniforms;
    if (accent.colour !== undefined) this.accentColour = accent.colour;
    if (accent.saturation !== undefined) this.accentSaturation = accent.saturation;
    if (accent.scheme !== undefined) {
      this.paletteScheme = SCHEMES[accent.scheme] ?? (SCHEMES.mono as PaletteScheme);
    }
    if (accent.strength !== undefined) uniforms.uAccentStrength!.value = accent.strength;
    if (accent.gain !== undefined) uniforms.uAccentGain!.value = accent.gain;

    const previous = this.palette;
    this.palette = buildPalette(this.paletteScheme, this.accentColour, this.accentSaturation);
    uniforms.tPalette!.value = this.palette;
    previous.dispose();
  }

  render(scene: THREE.Scene, camera: THREE.Camera, accentStrength: number): void {
    const renderer = this.renderer;

    renderer.setRenderTarget(this.sceneTarget);
    renderer.render(scene, camera);

    if (accentStrength > 0) {
      this.renderAccentMask(scene, camera);
      this.blit(this.blurHorizontal, this.accentTarget.texture, this.maskBlur1Target, 1, 0);
      this.blit(this.blurVertical, this.maskBlur1Target.texture, this.maskBlur2Target, 0, 1);
      this.blit(this.blurHorizontal, this.maskBlur2Target.texture, this.maskBlur1Target, 1, 0);
      this.blit(this.blurVertical, this.maskBlur1Target.texture, this.maskBlur2Target, 0, 1);
      this.blit(this.blurHorizontal, this.maskBlur2Target.texture, this.maskBlur1Target, 1, 0);
      this.blit(this.blurVertical, this.maskBlur1Target.texture, this.maskBlur2Target, 0, 1);
      this.blit(this.blurHorizontal, this.maskBlur2Target.texture, this.maskBlur1Target, 1, 0);
      this.blit(this.blurVertical, this.maskBlur1Target.texture, this.maskBlur2Target, 0, 1);
    }

    this.blit(this.blurHorizontal, this.sceneTarget.texture, this.blur1Target, 1, 0);
    this.blit(this.blurVertical, this.blur1Target.texture, this.scratch, 0, 1);

    this.blit(this.blurHorizontal, this.scratch.texture, this.blur1Target, 1, 0);
    this.blit(this.blurVertical, this.blur1Target.texture, this.blur2Target, 0, 1);

    this.blit(this.blurHorizontal, this.blur2Target.texture, this.blur1Target, 1, 0);
    this.blit(this.blurVertical, this.blur1Target.texture, this.blur3Target, 0, 1);

    renderer.setRenderTarget(null);
    renderer.render(this.composite.scene, this.composite.camera);
  }

  private collectAccentMeshes(scene: THREE.Scene): AccentMesh[] {
    const entries: AccentMesh[] = [];

    scene.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh || !mesh.layers.test(this.maskLayer)) return;
      const slot = typeof mesh.userData.slot === "number" ? mesh.userData.slot : 0;
      const variant = typeof mesh.userData.variant === "number" ? mesh.userData.variant : 0.5;
      const mask = new THREE.ShaderMaterial({
        vertexShader: MASK_VERTEX,
        fragmentShader: MASK_FRAGMENT,
        uniforms: { uSlot: { value: slot }, uVariant: { value: variant } },
      });
      entries.push({ mesh, original: mesh.material, mask });
    });

    return entries;
  }

  /** accent geometry added to the scene after the first frame needs a re-scan */
  invalidateAccent(): void {
    this.accentMeshes = null;
  }

  private renderAccentMask(scene: THREE.Scene, camera: THREE.Camera): void {
    const renderer = this.renderer;
    const background = scene.background;
    const autoClear = renderer.autoClear;

    renderer.getClearColor(this.clearColour);
    const alpha = renderer.getClearAlpha();
    renderer.setClearColor(0x000000, 1);
    scene.background = null;
    renderer.setRenderTarget(this.accentTarget);

    // pass one: whole scene in black, which lays down depth for occluders
    camera.layers.set(0);
    scene.overrideMaterial = this.occluderMaterial;
    renderer.render(scene, camera);

    // pass two: accent geometry draws its slot and variant, depth-tested against
    // pass one, so anything hidden behind the scene stays unmasked
    if (this.accentMeshes === null) this.accentMeshes = this.collectAccentMeshes(scene);
    const entries = this.accentMeshes;
    for (const entry of entries) entry.mesh.material = entry.mask;

    renderer.autoClear = false;
    camera.layers.set(1);
    scene.overrideMaterial = null;
    renderer.render(scene, camera);
    renderer.autoClear = autoClear;

    for (const entry of entries) entry.mesh.material = entry.original;

    camera.layers.set(0);
    scene.background = background;
    renderer.setClearColor(this.clearColour, alpha);
  }

  private blit(
    pass: FullscreenPass,
    source: THREE.Texture,
    target: THREE.WebGLRenderTarget,
    dx: number,
    dy: number,
  ): void {
    pass.material.uniforms.tSource!.value = source;
    (pass.material.uniforms.uDirection!.value as THREE.Vector2).set(dx, dy);
    this.renderer.setRenderTarget(target);
    this.renderer.render(pass.scene, pass.camera);
  }
}
